// Basic site analytics (disclosed in the footer). Fails silently if anything goes wrong.
// Open the site once with ?notrack to stop counting your own visits on that browser.

function storageGet(storage: Storage, key: string) {
  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

function storageSet(storage: Storage, key: string, value: string) {
  try {
    storage.setItem(key, value)
  } catch {}
}

function isTrackingEnabled() {
  if (process.env.NODE_ENV !== 'production') return false
  if (new URLSearchParams(window.location.search).has('notrack')) {
    storageSet(window.localStorage, 'notrack', '1')
  }
  return !storageGet(window.localStorage, 'notrack')
}

function send(data: object) {
  const body = JSON.stringify(data)
  if (navigator.sendBeacon && navigator.sendBeacon('/api/track', new Blob([body], { type: 'application/json' }))) {
    return
  }
  fetch('/api/track', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {})
}

function recordVisit() {
  // One visit per browser session, so refreshes don't inflate the count.
  if (storageGet(window.sessionStorage, 'visit-tracked')) return
  storageSet(window.sessionStorage, 'visit-tracked', '1')

  let timezone = ''
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {}

  send({
    type: 'visit',
    page: window.location.pathname + window.location.hash,
    referrer: document.referrer,
    screen: `${window.screen.width}x${window.screen.height}`,
    timezone,
  })
}

function trackEngagement() {
  let activeSince = document.visibilityState === 'visible' ? Date.now() : 0
  let activeMs = 0
  const sections = new Set<string>()
  let clicks: { at: string; label: string; href: string }[] = []

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.target.id) sections.add(entry.target.id)
      })
    },
    { threshold: 0.3 }
  )
  document.querySelectorAll('section[id]').forEach((el) => observer.observe(el))

  document.addEventListener(
    'click',
    (event) => {
      const link = (event.target as Element | null)?.closest?.('a')
      if (!link) return
      const label =
        link.textContent?.trim() ||
        link.getAttribute('aria-label') ||
        link.querySelector('img')?.getAttribute('alt') ||
        ''
      const href = link.hasAttribute('download') ? 'Resume download' : link.href
      clicks.push({ at: new Date().toISOString(), label, href })
    },
    true
  )

  function flush() {
    if (activeSince) {
      activeMs += Date.now() - activeSince
      activeSince = 0
    }
    const seconds = Math.round(activeMs / 1000)
    if (!seconds && !sections.size && !clicks.length) return
    send({ type: 'engagement', seconds, sections: Array.from(sections), clicks })
    activeMs = 0
    sections.clear()
    clicks = []
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush()
    } else {
      activeSince = Date.now()
    }
  })
  window.addEventListener('pagehide', flush)
}

export function trackVisit() {
  if (!isTrackingEnabled()) return
  recordVisit()
  trackEngagement()
}

export function trackContactSubmitted() {
  if (!isTrackingEnabled()) return
  send({ type: 'contact' })
}
